import { useMutation, useQuery } from '@apollo/client';
import {
  ContentBlock,
  Card,
  Stack,
  Toggle,
  Box,
  ButtonLink,
  Heading,
  Text,
  Strong,
} from 'braid-design-system';
import queryString from 'query-string';
import React, { useState, Fragment, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import CustomLoader from 'src/components/CustomLoader/CustomLoader';
import Header from 'src/components/Header/Header';
import LayoutBox from 'src/components/LayoutBox/LayoutBox';
import Routes from 'src/config/routes';
import { useAuth } from 'src/hooks/auth';
import { useCommand } from 'src/hooks/command';
import BUYER_DETAILS from 'src/queries/buyerDetails';
import { subscribeUser, unsubscribeUser } from 'src/services/api';
import type { Buyer } from 'src/types/buyer';
import { fireRecommendationFeedbackEvent } from 'src/utils/analytics';

import BusinessType from './components/BusinessType/BusinessType';
import ElementOverlay from './components/ElementOverlay/ElementOverlay';
import Industries from './components/Industries/Industries';
import Keywords from './components/Keywords/Keywords';
import PreferredLocation from './components/PreferredLocation/PreferredLocation';
import PriceRange from './components/PriceRange/PriceRange';
import Title from './components/Title/Title';
import POST_DRIP_EVENT from './queries/postDripEvent';

import * as styles from 'src/views/Preferences/components/Section/style.css';

const Preferences = () => {
  const initialRender = useRef(true);
  const [sendEmails, setSendEmails] = useState(true);
  const { data, error, refetch, loading } = useQuery<Buyer>(BUYER_DETAILS);
  if (error) {
    // TEMPORARY - 2021-12-20: Need to consolidate authentication to handle tokens,
    // buyers and sellers using GraphGL
    const currentUrl = window?.location.pathname + window?.location.search;
    window?.location.assign(
      `${Routes.SIGN_IN_URL}?ReturnUrl=${encodeURIComponent(currentUrl)}`,
    );
  }
  const userId = data?.buyer.userId as string;
  const { industries, districts, businessTypes, price, excludePoa, keywords } =
    data?.buyer?.recommendationPreferences ?? {
      industries: [],
      districts: [],
      businessTypes: [],
      keywords: [],
    };
  const subscriptionStatus = data?.buyer?.subscription?.subscriptionStatus;
  const { command: unsubscribe } = useCommand(unsubscribeUser);
  const urlLocation = useLocation();
  const { recommendation, listingId, source } = queryString.parseUrl(
    urlLocation.search,
  ).query;

  const [postEvent, { called }] = useMutation(POST_DRIP_EVENT);

  const { getAuthToken } = useAuth();

  useEffect(() => {
    const token = getAuthToken();
    // If the user has come from the buyer engagement email, send a drip event
    if (source === 'be0822' && Boolean(token) && !called) {
      postEvent({
        variables: {
          input: {
            action: 'engagement campaign aug 22 clickthrough',
          },
        },
      });
    }
  });

  const COPY_DEFAULT = {
    title: 'Manage Your Preferences',
    message: (
      <Fragment>
        <Text data={{ testid: 'message' }}>
          You can manage the type of business recommendations we email you by
          updating your preferences below.
        </Text>
        <Text>
          Simply complete your preferences and we&apos;ll do the rest. The more
          specific you can be, the better the match. It&apos;s that simple!
        </Text>
      </Fragment>
    ),
  };
  const COPY_RELEVANT = {
    title: 'Thanks for your feedback!',
    message: (
      <Text data={{ testid: 'message' }}>
        We&apos;ll continue to send you recommendations based on your
        preferences below. You can update or add to them at any time{' '}
        <Strong>via the link in your profile</Strong>. This will ensure you only
        get the most relevant businesses sent to you.
      </Text>
    ),
  };

  const pageCopy = recommendation === 'relevant' ? COPY_RELEVANT : COPY_DEFAULT;

  useEffect(() => {
    if (subscriptionStatus === 'Subscribed') {
      setSendEmails(true);
    } else {
      setSendEmails(false);
    }
  }, [subscriptionStatus]);

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
      if (recommendation) {
        const buyer = !loading ? data?.buyer : null;
        fireRecommendationFeedbackEvent(
          recommendation === 'relevant',
          String(buyer?.userId),
          String(listingId),
        );
      }
    } else if (userId) {
      if (sendEmails) {
        subscribeUser(userId);
      } else {
        unsubscribe(userId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendEmails]);

  return (
    <Fragment>
      <Header title="Customise your recommendations" />
      <Box paddingBottom="xlarge" width="full">
        <Title />
      </Box>
      <ContentBlock>
        <Stack space="medium">
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            paddingBottom="medium"
          >
            <Heading level="2">{pageCopy.title}</Heading>
          </Box>
          <Box className={styles.sectionContainer}>
            <Stack space="medium">
              <Fragment>{pageCopy.message}</Fragment>
              {!loading && (
                <Toggle
                  id="emailSubscriptionToggle"
                  onChange={() => setSendEmails(!sendEmails)}
                  label="Send me email recommendations"
                  on={sendEmails}
                />
              )}
            </Stack>
          </Box>
          {loading ? (
            <CustomLoader />
          ) : (
            <Fragment>
              <ElementOverlay visible={!sendEmails}>
                <Card>
                  <Stack space="medium">
                    <Keywords keywords={keywords} refetch={refetch} />
                    <Industries industries={industries} refetch={refetch} />
                    <PreferredLocation
                      districts={districts}
                      refetch={refetch}
                    />
                    <PriceRange
                      price={price}
                      excludePoa={Boolean(excludePoa)}
                    />
                    <BusinessType businessTypes={businessTypes} />
                    <Box paddingBottom="medium">
                      <LayoutBox maxWidth={200}>
                        <ButtonLink
                          href="/buyer/preferences/submitted"
                          variant="solid"
                        >
                          Done
                        </ButtonLink>
                      </LayoutBox>
                    </Box>
                  </Stack>
                </Card>
              </ElementOverlay>
            </Fragment>
          )}
        </Stack>
      </ContentBlock>
    </Fragment>
  );
};

export default Preferences;
